import { useState } from 'react';
import { PlusCircledIcon } from '@radix-ui/react-icons';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';

import FilterCard from './FilterCard';

const booleanList = [
  {
    _id: 1,
    value: 'true',
    label: 'TRUE',
  },
  {
    _id: 2,
    value: 'false',
    label: 'FALSE',
  },
];

const AdvancedFilterSheet = ({
  isOpen,
  onOpenChange,
  filters,
  onFiltersChange,
  title = 'Advanced Filters',
  triggerButtonText = 'Advanced Filter',
  fieldList = [],
  enumDataSources = {},
  validationRules = {},
  operatorsList = {},
}) => {
  const [tempFilters, setTempFilters] = useState([]);

  // Ref: Below code is for reference
  // const [tempAdvancedFilters, setTempAdvancedFilters] = useState([
  //   [
  //     {
  //       field: 'full_name',
  //       operator: 'contains_exactly',
  //       value: 'Amirth',
  //       // type -> text / number / enum / boolean
  //     },
  //     {
  //       field: 'ig_username',
  //       operator: 'contains',
  //       value: 'amir',
  //     },
  //   ],
  //   [
  //     {
  //       field: 'is_verified',
  //       operator: 'is',
  //       value: 'true',
  //     },
  //     {
  //       field: 'tag',
  //       operator: 'is_one_of',
  //       value: [],
  //     },
  //   ],
  // ]);

  const totalFiltersCount = filters.reduce(
    (acc, group) => acc + group.length,
    0
  );

  // Function to close the sheet
  const handleCancel = () => {
    onOpenChange(!isOpen);
  };

  // Function to open the sheet and create a copy of the existing filters
  const handleOpen = () => {
    onOpenChange(!isOpen);
    setTempFilters(filters);
  };

  // Function to validate the selected filters before saving
  const validateFilters = () => {
    return tempFilters.every((group) =>
      group.every((filter) => {
        // Use custom validation rules if provided
        if (validationRules[filter.field]) {
          return validationRules[filter.field](filter.value);
        }

        // Default validation
        if (['tag_ids', 'plan', 'user_id'].includes(filter.field)) {
          return Array.isArray(filter.value) && filter.value.length > 0;
        } else {
          return typeof filter.value === 'string' && filter.value.trim() !== '';
        }
      })
    );
  };

  // Function to save the filters
  const handleSave = () => {
    if (validateFilters()) {
      onFiltersChange(tempFilters);
    } else {
      toast.error('Update the conditions properly !');
    }

    onOpenChange(!isOpen);
  };

  // Function to add a new filter
  const handleNewFilter = () => {
    setTempFilters([
      [
        {
          field: '',
          operator: '',
          value: '',
        },
      ],
    ]);
  };

  // Function to add a new AND Condition
  const handleANDCondition = (outerIndex) => {
    setTempFilters((prevState) => {
      const newState = [...prevState];

      // Add the new filter to the inner array at the specified index
      newState[outerIndex] = [
        ...newState[outerIndex],
        {
          field: '',
          operator: '',
          value: '',
        },
      ];

      return newState;
    });
  };

  // Function to add a new OR Condition
  const handleORCondition = () => {
    setTempFilters([
      ...tempFilters,
      [
        {
          field: '',
          operator: '',
          value: '',
        },
      ],
    ]);
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleCancel}>
      <SheetTrigger asChild>
        <Button onClick={handleOpen}>
          {triggerButtonText} ({totalFiltersCount})
        </Button>
      </SheetTrigger>
      <SheetContent className='flex w-[400px] flex-col sm:w-[540px] sm:max-w-md'>
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>
        <div className='flex overflow-y-auto'>
          <ScrollArea className='w-full'>
            {tempFilters.length > 0 ? (
              <div className='w-full'>
                {tempFilters.map((outerFilter, outerIndex) => (
                  <div key={outerIndex}>
                    <div className='w-full rounded-md border p-2'>
                      {outerFilter.map((condition, innerIndex) => (
                        <div key={`${outerIndex}_${innerIndex}`}>
                          <div>
                            <div key={innerIndex}>
                              <FilterCard
                                setTempFilters={setTempFilters}
                                outerIndex={outerIndex}
                                innerIndex={innerIndex}
                                propertyField={condition.field}
                                propertyOperator={condition.operator}
                                propertyValue={condition.value}
                                fieldList={fieldList}
                                operatorList={operatorsList}
                                booleanList={booleanList}
                                enumDataSources={enumDataSources}
                              />
                            </div>
                          </div>
                          {innerIndex !==
                            tempFilters[outerIndex].length - 1 && (
                            <div className='relative ml-4 py-2 text-xs text-foreground'>
                              <span className='absolute inset-0 left-4 h-full w-px bg-border'></span>
                              <span className='relative rounded-md border bg-background px-1'>
                                and
                              </span>
                            </div>
                          )}
                          {innerIndex ===
                            tempFilters[outerIndex].length - 1 && (
                            <Button
                              variant='secondary'
                              size='xs'
                              className='mt-3'
                              onClick={() => handleANDCondition(outerIndex)}
                            >
                              AND
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                    {outerIndex !== tempFilters.length - 1 && (
                      <div className='relative ml-5 py-2 text-xs text-foreground'>
                        <span className='absolute inset-0 left-3 h-full w-px bg-border'></span>
                        <span className='relative rounded-md border bg-background px-1'>
                          or
                        </span>
                      </div>
                    )}
                  </div>
                ))}
                <Button
                  variant='secondary'
                  size='xs'
                  className='mt-3'
                  onClick={handleORCondition}
                >
                  OR
                </Button>
              </div>
            ) : (
              <Button className='m-auto' onClick={handleNewFilter}>
                <PlusCircledIcon className='mr-2 h-4 w-4' /> Add Filter
              </Button>
            )}
          </ScrollArea>
        </div>
        <SheetFooter className='mt-auto flex justify-end'>
          <SheetClose asChild>
            <Button variant='outline'>Cancel</Button>
          </SheetClose>
          <Button onClick={handleSave}>Save</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default AdvancedFilterSheet;
